import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { authSuccess } from '../redux/userRelated/userSlice';

const STORAGE_KEY = 'fee_receipt_config';

const defaultConfig = {
    header: {
        schoolName: '',
        address: '',
        contact: '',
        email: '',
        logo: '',
        panNumber: ''
    },
    footer: 'Thank you for your payment. Please keep this receipt for future reference.',
    showFields: {
        tuition: true,
        admission: true,
        annual: true,
        security: true,
        examination: true,
        computer: true,
        transportation: true,
        hostel: true,
        stationary: true,
        sports: true,
        transferCertificate: true,
        characterCertificate: true,
        specialTraining: true,
        meal: true,
        educationalTour: true,
        eca: true,
        educationalMaterial: true,
        extras: true,
        dues: true,
        total: true
    },
    customFields: []
};

/**
 * Custom hook to manage receipt configuration with MongoDB & localStorage persistence.
 * Returns [config, updateConfig] where updateConfig merges changes into the config.
 * 
 * @param {object} overrides - optional overrides for default values (e.g. currentUser info)
 */
const useReceiptConfig = (overrides = {}) => {
    const { currentUser } = useSelector(state => state.user);

    const [config, setConfig] = useState(() => {
        try {
            // First try to get from currentUser if it exists (from DB)
            if (currentUser && currentUser.receiptConfig && Object.keys(currentUser.receiptConfig).length > 0) {
                const dbConfig = currentUser.receiptConfig;
                return {
                    ...defaultConfig,
                    ...dbConfig,
                    header: { ...defaultConfig.header, ...dbConfig.header },
                    showFields: { ...defaultConfig.showFields, ...dbConfig.showFields },
                    customFields: dbConfig.customFields || []
                };
            }

            // Fallback to localStorage
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                return {
                    ...defaultConfig,
                    ...parsed,
                    header: { ...defaultConfig.header, ...parsed.header },
                    showFields: { ...defaultConfig.showFields, ...parsed.showFields },
                    customFields: parsed.customFields || []
                };
            }
        } catch (e) {
            console.warn('Failed to load receipt config:', e);
        }

        // First time — use defaults with overrides
        return {
            ...defaultConfig,
            header: { ...defaultConfig.header, ...overrides }
        };
    });

    // Sync from currentUser if it updates later
    useEffect(() => {
        if (currentUser && currentUser.receiptConfig && Object.keys(currentUser.receiptConfig).length > 0) {
            setConfig(prev => {
                const dbConfig = currentUser.receiptConfig;
                return {
                    ...prev,
                    ...dbConfig,
                    header: { ...prev.header, ...dbConfig.header },
                    showFields: { ...prev.showFields, ...dbConfig.showFields },
                    customFields: dbConfig.customFields || prev.customFields || []
                };
            });
        }
    }, [currentUser]);

    const dispatch = useDispatch();
    const timeoutRef = require('react').useRef(null);

    const updateConfig = useCallback((newConfig) => {
        setConfig(prev => {
            const merged = {
                ...prev,
                ...newConfig,
                header: { ...prev.header, ...(newConfig.header || {}) },
                showFields: { ...prev.showFields, ...(newConfig.showFields || {}) },
                customFields: newConfig.customFields !== undefined ? newConfig.customFields : prev.customFields
            };

            // Save to localStorage immediately for fast UI
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            } catch (e) {
                console.warn('Failed to save receipt config to localStorage:', e);
            }

            // Debounce save to MongoDB
            if (currentUser && currentUser._id) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(async () => {
                    try {
                        const response = await axios.put(`${process.env.REACT_APP_BASE_URL}/Admin/ReceiptConfig/${currentUser._id}`, {
                            receiptConfig: merged
                        });
                        
                        // If backend update was successful, update Redux state to persist across refreshes
                        if (response.data && response.data._id) {
                            dispatch(authSuccess(response.data));
                        }
                    } catch (error) {
                        console.error("Failed to persist receipt config to DB:", error);
                    }
                }, 1000); // 1 second debounce
            }

            return merged;
        });
    }, [currentUser, dispatch]);

    return [config, updateConfig];
};

export default useReceiptConfig;
